import {
    AppId,
    FCX,
    includesInLC,
    ProductLogo,
    usePlatformFeatureFlag
} from "@datassential/platform-ui-lib";
import { GrNext, GrPrevious } from "react-icons/gr";
import cn from "classnames";
import './SocialPosts.scss';
import { SocialColor } from "@models/FoodSocial";
import { ReactNode, useMemo, useState } from "react";
import { ISocialPost } from "@models/SocialPosts";
import SocialPostCard from "@components/SocialPostCard/SocialPostCard";

const Slide: FCX<{
    data: ISocialPost & { index: number };
    slideIndex: number;
    word?: string;
    association?: string;
    blurClassName?: string;
    isDark?: boolean;
    showFollowers?: boolean;
}> = ({
    data,
    slideIndex,
    word,
    association,
    blurClassName,
    showFollowers,
    isDark,
}) => {
    const isMinimalFiltersFeatureEnabled = usePlatformFeatureFlag('Snap Social Listening Minimal Filters');
    const { sentiment, index, url } = data;

    const color = useMemo(
        () => {
            if (isMinimalFiltersFeatureEnabled) {
                return SocialColor.Neutral;
            } else if (includesInLC(sentiment, 'positive')) {
                return SocialColor.Positive;
            } else if (includesInLC(sentiment, 'negative')) {
                return SocialColor.Negative;
            }
            return SocialColor.Neutral;
        },
        [sentiment, isMinimalFiltersFeatureEnabled]
    );

    return (
        <div
            className={cn("SocialPosts__item", `slide-${slideIndex}`)}
            data-testid={`blockSocialPostExamplesItem-${index}`}
        >
            <div
                className="SocialPosts__item-inner"
                style={{
                    borderColor: color,
                }}
            >
                {!!url && (
                    <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="SocialPosts__item-link"
                        data-testid="linkSocialPostExamplesItemUrl"
                    >
                        {url}
                    </a>
                )}
                <div className={cn("SocialPosts__item-content", blurClassName)}>
                    <SocialPostCard
                        post={data}
                        showFollowers={showFollowers}
                        isDark={isDark}
                        word={word}
                        association={association}
                        testIdPrefix="blockSocialPostExamplesItem"
                    />
                </div>
            </div>
        </div>
    );
};

const SocialPosts: FCX<{
    data: ISocialPost[];
    isLoading: boolean;
    isDark?: boolean;
    word?: string;
    association?: string;
    blurClassName?: string;
    showFollowers?: boolean;
    prevIcon?: ReactNode;
    nextIcon?: ReactNode;
}> = ({
    data: rawData,
    isLoading,
    isDark,
    word,
    association,
    blurClassName,
    showFollowers,
    prevIcon,
    nextIcon,
}) => {
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    const data = useMemo(
        () => {
            const items = rawData.map((item, index) => ({ ...item, index }));
            if (items.length >= 5 || items.length < 3) return items;
            return [
                ...items,
                ...items.map((item, i) => ({ ...item, id: `${item.id}-dup-${i}` })),
            ];
        },
        [rawData]
    );

    const visibleSlides = useMemo(
        () => {
            if (data.length === 0) return [];

            const tripleData = [...data, ...data, ...data];

            return tripleData.slice(data.length + currentSlideIndex - 1, data.length + currentSlideIndex + 4);
        },
        [data, currentSlideIndex],
    );


    return (
        <div className={cn("SocialPosts appears del-1", isDark && "is-dark")}>
            {isLoading && (
                <div className="spinner spinner-blink is-small">
                    <ProductLogo appId={AppId.Snap} type="regular"/>
                </div>
            )}
            {!isLoading && data.length > 0 && (
                <>
                    <div
                        className={cn("SocialPosts__nav is-prev")}
                        onClick={() => {
                            if (!isAnimating) {
                                setCurrentSlideIndex(currentSlideIndex === 0 ? data.length - 1 : currentSlideIndex - 1);
                                setIsAnimating(true);
                                setTimeout(() => setIsAnimating(false), 550);
                            }
                        }}
                        data-testid="buttonSocialPostExamplesNavPrev"
                    >
                        {prevIcon || <GrPrevious/>}
                    </div>
                    <div
                        className={cn("SocialPosts__nav is-next")}
                        onClick={() => {
                            if (!isAnimating) {
                                setCurrentSlideIndex(currentSlideIndex === data.length - 1 ? 0 : currentSlideIndex + 1);
                                setIsAnimating(true);
                                setTimeout(() => setIsAnimating(false), 550);
                            }
                        }}
                        data-testid="buttonSocialPostExamplesNavNext"
                    >
                        {nextIcon || <GrNext/>}
                    </div>
                    <div className={cn("SocialPosts__inner", isAnimating && 'is-ov-hid')}>
                        {data.length >= 3 ? (
                            visibleSlides.map((item, index) => (
                                <Slide
                                    key={item.id}
                                    data={item}
                                    slideIndex={index}
                                    word={word}
                                    association={association}
                                    blurClassName={blurClassName}
                                    isDark={isDark}
                                    showFollowers={showFollowers}
                                />
                            ))
                        ) : (
                            data.map((item, index) => (
                                <Slide
                                    key={item.id}
                                    data={item}
                                    slideIndex={index + 1}
                                    word={word}
                                    association={association}
                                    blurClassName={blurClassName}
                                    isDark={isDark}
                                    showFollowers={showFollowers}
                                />
                            ))
                        )}
                    </div>
                </>
            )}
            {!isLoading && data.length === 0 && (
                <div className="SocialPosts__no-results">
                    No social posts found.
                </div>
            )}
        </div>
    );
};

export default SocialPosts;
